import { useMemo } from "react";
import { AppState } from "@Store/index";
import { shallowEqual, useSelector } from "react-redux";
import SectionContainer from "../SectionContainer";
import wrapperHOC from "@Utils/wrapperHOC";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import ChecktagResidents from "@Icons/ChecktagResidents";
import CircleInfo from "@Icons/CircleInfo";
import CircleCheck from "@Icons/CircleCheck";
import useTranslationHook from "hooks/useTranslationHook";

import styles from "./MeetTheResidents.module.scss";
import config from "@Config/index";

interface MeetTheResidentsProps {
  isDesktop?: boolean;
}

// Asian country codes (ISO 3166-1 alpha-2)
const ASIAN_COUNTRY_CODES = new Set([
  "AF",
  "AM",
  "AZ",
  "BH",
  "BD",
  "BT",
  "BN",
  "KH",
  "CN",
  "CY",
  "GE",
  "IN",
  "ID",
  "IR",
  "IQ",
  "IL",
  "JP",
  "JO",
  "KZ",
  "KW",
  "KG",
  "LA",
  "LB",
  "MY",
  "MV",
  "MN",
  "MM",
  "NP",
  "KP",
  "OM",
  "PK",
  "PS",
  "PH",
  "QA",
  "SA",
  "SG",
  "KR",
  "LK",
  "SY",
  "TW",
  "TJ",
  "TH",
  "TL",
  "TR",
  "TM",
  "AE",
  "UZ",
  "VN",
  "YE",
]);

// Mapping: country code → demonym used in nat_dist origin field
const COUNTRY_TO_DEMONYM: Record<string, string> = {
  IN: "Indian",
  PK: "Pakistani",
  BD: "Bangladeshi",
  NP: "Nepali",
  LK: "Sri Lankan",
  CN: "Chinese",
  JP: "Japanese",
  KR: "Korean",
  TH: "Thai",
  VN: "Vietnamese",
  MY: "Malaysian",
  ID: "Indonesian",
  PH: "Filipino",
  SG: "Singaporean",
  MM: "Burmese",
  KH: "Cambodian",
  LA: "Laotian",
  BN: "Bruneian",
  TW: "Taiwanese",
  MN: "Mongolian",
  KZ: "Kazakh",
  UZ: "Uzbek",
  KG: "Kyrgyz",
  TJ: "Tajik",
  TM: "Turkmen",
  AZ: "Azerbaijani",
  GE: "Georgian",
  AM: "Armenian",
  AF: "Afghan",
  IR: "Iranian",
  IQ: "Iraqi",
  SA: "Saudi",
  AE: "Emirati",
  QA: "Qatari",
  KW: "Kuwaiti",
  BH: "Bahraini",
  OM: "Omani",
  YE: "Yemeni",
  JO: "Jordanian",
  LB: "Lebanese",
  SY: "Syrian",
  PS: "Palestinian",
  IL: "Israeli",
  TR: "Turkish",
  CY: "Cypriot",
  BT: "Bhutanese",
  MV: "Maldivian",
  TL: "Timorese",
  KP: "North Korean",
};

/**
 * Get the filtered university name from localStorage.
 * Stored as "University Name - Location" by the search filter.
 */
const getFilteredUniversityName = (): string | null => {
  if (typeof localStorage === "undefined") return null;
  const stored = localStorage.getItem("filtered_university_and_region");
  if (!stored) return null;
  // Format: "University Name - Location"
  const dashIndex = stored.lastIndexOf(" - ");
  return dashIndex > 0 ? stored.substring(0, dashIndex).trim() : stored.trim();
};

const MeetTheResidents = ({ isDesktop = false }: MeetTheResidentsProps) => {
  const { t } = useTranslationHook();
  const isMeetTheResidentsEnabled = true;
  const { insights, userCountryCode } = useSelector(
    ({ propertyPageData, appData }: AppState) => ({
      insights: propertyPageData?.data?.meta?.property_residents_insights,
      userCountryCode: appData.country,
    }),
    shallowEqual,
  );
  const nat_dist = insights?.nat_dist;
  const uni_dist = insights?.uni_dist;
  const all_uni_dist = insights?.all_uni_dist;

  // Handle university filter backfill + "Others" aggregation
  // Produces a display-ready list with at most 5 entries (backfill case) or 4 entries (normal case).
  const processedUniDist = useMemo(() => {
    // Case 1: No data — return as-is (undefined / empty array).
    if (!uni_dist || uni_dist.length === 0) return uni_dist;

    const entries = [...uni_dist];
    const filteredUniName = getFilteredUniversityName();

    // Check if the filtered university already appears anywhere in uni_dist.
    const filteredUniIndex = filteredUniName
      ? entries.findIndex((e) => e.name.toLowerCase() === filteredUniName.toLowerCase())
      : -1;
    const isFilteredUniInEntries = filteredUniIndex !== -1;
    const isFilteredUniInTop3 = filteredUniIndex >= 0 && filteredUniIndex < 3;

    if (filteredUniName && isFilteredUniInEntries && !isFilteredUniInTop3 && entries.length > 3) {
      // Case 2: Filtered university exists in uni_dist but is outside the top 3.
      // Surface it at position 4 so the user can see the uni they searched for,
      // and collapse everything else (index 3+ excluding filtered uni) into "Others".
      // Result: [Top1, Top2, Top3, FilteredUni, Others]
      const top3 = entries.slice(0, 3);
      const filteredEntry = entries[filteredUniIndex];
      const othersPercentage = entries
        .slice(3)
        .filter((_, i) => i + 3 !== filteredUniIndex)
        .reduce((sum, e) => sum + e.percentage, 0);
      return [
        ...top3,
        filteredEntry,
        ...(othersPercentage > 0
          ? [
              {
                name: t("propertypage.meettheresidents.others", { defaultValue: "Others" }),
                percentage: othersPercentage,
              },
            ]
          : []),
      ];
    }

    if (filteredUniName && !isFilteredUniInEntries) {
      // Filtered university is NOT in uni_dist.
      // Check if it exists in all_uni_dist (universities from bookforms/bookings not in uni_dist).
      const allUniMatch =
        all_uni_dist && all_uni_dist.length > 0
          ? all_uni_dist.find((e: any) => e.name.toLowerCase() === filteredUniName.toLowerCase())
          : null;

      if (allUniMatch) {
        // Case 3: Filtered university found in all_uni_dist.
        // Recalculate all percentages using combined total count (top 3 + custom uni + others).
        const top3 = entries.slice(0, 3);
        const customUniCount = allUniMatch.count || 0;
        const othersCount = entries.slice(3).reduce((sum, e) => sum + (e.count || 0), 0);
        const combinedTotal =
          top3.reduce((sum, e) => sum + (e.count || 0), 0) + customUniCount + othersCount;

        if (combinedTotal > 0) {
          const recalculatedTop3 = top3.map((e) => ({
            ...e,
            percentage: Math.round(((e.count || 0) / combinedTotal) * 100),
          }));
          const customUniPercentage = Math.round((customUniCount / combinedTotal) * 100);
          const othersPercentage = Math.round((othersCount / combinedTotal) * 100);

          return [
            ...recalculatedTop3,
            { name: allUniMatch.name, percentage: customUniPercentage, count: customUniCount },
            ...(othersPercentage > 0
              ? [
                  {
                    name: t("propertypage.meettheresidents.others", { defaultValue: "Others" }),
                    percentage: othersPercentage,
                  },
                ]
              : []),
          ];
        }
      }

      // Case 4: Filtered university is NOT in uni_dist AND NOT in all_uni_dist.
      // Show the filtered university at position 4 using the 4th university's percentage,
      // and "Others" shows only the percentage of the 5th university onward.
      if (entries.length > 3) {
        const top3 = entries.slice(0, 3);
        const fourthEntry = entries[3];
        const othersPercentage = entries.slice(4).reduce((sum, e) => sum + e.percentage, 0);
        return [
          ...top3,
          { name: filteredUniName, percentage: fourthEntry.percentage },
          ...(othersPercentage > 0
            ? [
                {
                  name: t("propertypage.meettheresidents.others", { defaultValue: "Others" }),
                  percentage: othersPercentage,
                },
              ]
            : []),
        ];
      }
    }

    // Case 5: No filtered uni (or it's already in top 3), more than 3 universities.
    // Show the top 3 and aggregate the rest into "Others".
    // Result: [Top1, Top2, Top3, Others]
    if (entries.length > 3) {
      const top3 = entries.slice(0, 3);
      const othersPercentage = entries.slice(3).reduce((sum, e) => sum + e.percentage, 0);
      return [
        ...top3,
        {
          name: t("propertypage.meettheresidents.others", { defaultValue: "Others" }),
          percentage: othersPercentage,
        },
      ];
    }

    // Case 6: 3 or fewer universities, no filter active — return as-is.
    return entries;
  }, [uni_dist, all_uni_dist, t]);

  if (__CLIENT__ && !isMeetTheResidentsEnabled) {
    return null;
  }

  if (!insights || typeof insights !== "object" || !insights.visible) {
    return null;
  }

  const hasUniversities = Boolean(processedUniDist && processedUniDist.length > 0);

  // Section only renders when uni_dist has data
  if (!hasUniversities) {
    return null;
  }

  // Determine badge: show only if user is from an Asian country
  // and the top origin in nat_dist matches the user's country demonym
  let badgeText: string | null = null;

  if (
    userCountryCode &&
    ASIAN_COUNTRY_CODES.has(userCountryCode) &&
    nat_dist &&
    nat_dist.length > 0
  ) {
    const topOrigin = nat_dist[0]; // Already sorted by highest percentage from backend
    const userDemonym = COUNTRY_TO_DEMONYM[userCountryCode];
    if (userDemonym && topOrigin.origin.toLowerCase() === userDemonym.toLowerCase()) {
      badgeText = config.IS_CHINA_APP
        ? t("中国学生首选")
        : t("propertypage.meettheresidents.preferredbystudentstag", {
            origin: topOrigin.origin,
          });
    }
  }

  const iconSize = isDesktop ? 16 : 12;

  const hasMeetTheResidentsData = Boolean(
    insights?.visible && (insights?.uni_dist?.length ?? 0) >= 3,
  );
  if (!hasMeetTheResidentsData) return null;
  return (
    <SectionContainer
      title={t("propertypage.components.meettheresidents")}
      id="meet_the_residents"
      className={formatClassNames(styles.container, isDesktop && styles.desktop)}
      isDesktop={isDesktop}
      showTitleLine
      headingLevel={2}
    >
      <div className={styles.content}>
        <div className={styles.badgeRow} data-testid="resident-fact-badges">
          <div className={styles.badgeChip} data-testid="resident-fact-badge-multicultural">
            <ChecktagResidents width={iconSize} height={iconSize} />
            <span>{t("propertypage.components.multiculturalstudentcommunity")}</span>
          </div>
          {badgeText && (
            <div className={styles.badgeChip} data-testid="resident-fact-badge">
              <ChecktagResidents width={iconSize} height={iconSize} />
              <span>{badgeText}</span>
            </div>
          )}
        </div>

        <div
          className={formatClassNames(styles.universityGrid, isDesktop && styles.desktopGrid)}
          data-testid="university-distribution"
        >
          {(processedUniDist || []).map((entry: any) => (
            <div key={entry.name} className={styles.universityEntry}>
              <div className={styles.universityHeader}>
                <span className={styles.universityName}>{entry.name}</span>
                <span className={styles.universityPercentage}>{entry.percentage}%</span>
              </div>
              <div className={styles.progressTrack}>
                <div
                  className={styles.progressBar}
                  style={{ width: `${entry.percentage}%` }}
                  data-testid={`progress-bar-${entry.name}`}
                />
              </div>
            </div>
          ))}
        </div>

        <hr
          className={formatClassNames(
            styles.dashedSeparator,
            isDesktop && styles.dashedSeparatorDesktop,
          )}
        />

        <div className={styles.footer} data-testid="data-source-footer">
          <span className={styles.infoIcon}>
            {isDesktop ? (
              <CircleCheck fill="#4B5563" />
            ) : (
              <CircleInfo color="#4B5563" width={iconSize} height={iconSize} />
            )}
          </span>
          <span>{t("Data Source: As per historical booking data")}</span>
        </div>
      </div>
    </SectionContainer>
  );
};

export default wrapperHOC(MeetTheResidents, {
  componentName: "MeetTheResidents-PropertyPage",
  showForChina: true,
});
